import { ConfirmDialog, LoadingScreen, TimelineInteractiveCard, useUncontrolledState } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import type { TimelineCardBaseProps } from "../types";
import { useAuth, useTellescopeMutation, useTellescopeQuery, type FormResponseGroup } from "@tellescope/tanstack-query";
import { useCallback, useMemo, useState } from "react";
import { formatDate, formatDateTime } from "@tellescope/utils";
import { Copy, Eye, MessageSquare, RefreshCw, Tag, Trash2 } from "lucide-react";
import { FormResponse, FormResponses, FormResponsePrintablePDF } from "../../../../forms-responses";
import { usePDFActions } from "../../../../../hooks";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AddTagContent } from "../AddTagContent";

type ContentType = "view-responses";

export interface FormResponseTimelineCardProps extends TimelineCardBaseProps<FormResponseGroup> {}

export const FormResponseTimelineCard = ({
  currentEnduserId,
  enduserName,
  item,
  className,
  open,
  onOpenChange,
}: FormResponseTimelineCardProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Whether the card is expanded is controlled by the block (survives virtualized unmount); the
  // selected response within stays local, resetting when the card is collapsed.
  const [isOpen, setIsOpen] = useUncontrolledState<boolean>({
    state: open ?? false,
    setState: onOpenChange && ((next) => onOpenChange(next instanceof Function ? next(open ?? false) : next)),
  });
  const [selectedResponseId, setSelectedResponseId] = useState<string>();
  const contentToDisplay: ContentType | undefined = isOpen ? "view-responses" : undefined;
  const setContentToDisplay = (next: ContentType | undefined) => setIsOpen(next !== undefined);
  const [isDeleting, setIsDeleting] = useState(false);
  const selectedResponse = useMemo(
    () => item.responses.find((response) => response.id === selectedResponseId),
    [item.responses, selectedResponseId]
  );

  const { data: form } = useTellescopeQuery({
    model: "forms",
    method: "getOne",
    request: item.formId,
  });

  // Get theme data early, outside of PDF view
  const { data: { theme } = {} } = useTellescopeQuery({
    model: "organizations",
    method: "get_theme",
    request: {
      businessId: item.responses[0]?.businessId ?? "", // Use first response's businessId
    },
    enabled: !!item.responses[0]?.businessId,
  });

  const updateFormMutation = useTellescopeMutation({
    model: "forms",
    method: "updateOne",

    optimisticUpdates: true,
  });

  // PDF generation actions
  const pdfActions = usePDFActions({
    filename: `${item.formTitle.replace(/\W/g, "-")}-${selectedResponse?.submittedAt?.toISOString() ?? "latest"}`,
    enduserId: currentEnduserId,
    logoUrl: theme?.logoURL, // Pass logo URL to convert to data URL
    uploadSuccessMessage: t("PDF saved to profile"),
    uploadErrorMessage: t("Failed to save PDF"),
  });

  const startDiscussion = useCallback(() => {
    const params = new URLSearchParams({ enduserId: currentEnduserId, subject: "Form Discussion" });
    navigate(`/messaging/inbox/chat/new?${params.toString()}`);
  }, [navigate, currentEnduserId]);

  const syncMutation = useTellescopeMutation({
    model: "form_responses",
    method: "push_to_EHR",
  });

  const handleSyncResponses = useCallback(async () => {
    const results = await Promise.allSettled(
      item.responses.map(async (response) => {
        syncMutation.mutateAsync({
          id: response.id,
          addedResponses: response.responses,
        });
      })
    );
    if (results.some((result) => result.status === "rejected")) {
      toast.error(t("Some responses were unable to be synced"));
    } else {
      toast.success(t("Form responses synced!"));
    }
  }, [item.responses, syncMutation, t]);

  const { selectedOrganization: org } = useAuth();
  const ehrNames = useMemo(() => {
    const ehrs: string[] = [];
    if (org?.hasConnectedHealthie) {
      ehrs.push("Healthie");
    }
    if (org?.hasConnectedElation) {
      ehrs.push("Elation");
    }
    if (org?.hasConnectedCanvas) {
      ehrs.push("Canvas");
    }
    if (org?.hasConnectedAthena) {
      ehrs.push("Athena");
    }
    return ehrs.join(", ");
  }, [org?.hasConnectedAthena, org?.hasConnectedCanvas, org?.hasConnectedElation, org?.hasConnectedHealthie]);

  const recentActivity = useMemo(() => {
    const activity: { content: string; timestamp: Date }[] = [];
    if (form?.updatedAt) {
      activity.push({
        content: `${t("Edited on:")} ${formatDate(form?.updatedAt)}`,
        timestamp: form?.updatedAt,
      });
    }
    const latestResponseTimestamp = item.responses[0]?.submittedAt;
    if (latestResponseTimestamp) {
      activity.push({
        content: `${t("Responded at: ")} ${formatDateTime(latestResponseTimestamp)}`,
        timestamp: latestResponseTimestamp,
      });
    }

    activity.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    return activity[0]?.content;
  }, [form, item, t]);

  return (
    <>
      <TimelineInteractiveCard
        icon="form_response"
        title={item.formTitle}
        timestamp={item.responses[0]?.submittedAt ?? item.responses[0]?.createdAt ?? new Date()}
        disablePreview
        onHeaderClick={contentToDisplay ? undefined : () => setContentToDisplay("view-responses")}
        preview={
          form
            ? {
                content: form.description ?? "",
                recentActivity,
              }
            : undefined
        }
        tags={form?.tags}
        actions={[
          [
            {
              label: t("View Responses"),
              icon: Eye,
              onClick: () => {
                setContentToDisplay("view-responses");
              },
            },
            {
              label: t("Delete"),
              icon: Trash2,
              onClick: () => setIsDeleting(true),
            },
          ],
          [
            {
              label: t("Sync to {{ehrNames}}", { ehrNames }),
              icon: RefreshCw,
              onClick: () => {
                handleSyncResponses();
              },
              hidden: !ehrNames,
            },
            {
              label: t("Start Discussion"),
              icon: MessageSquare,
              onClick: startDiscussion,
            },
          ],
          [
            {
              label: t("Copy ID"),
              icon: Copy,
              onClick: () => {
                window.navigator.clipboard.writeText(item.formId);
              },
            },
          ],
          [
            {
              label: t("Add Tag"),
              icon: Tag,
              submenu: (
                <AddTagContent
                  currentTags={form?.tags ?? []}
                  onTagClick={async (tag) => {
                    if (!form) return;
                    if (!form?.tags?.includes(tag)) {
                      await updateFormMutation.mutateAsync(
                        {
                          id: form.id,
                          tags: [...(form.tags ?? []), tag],
                        },
                        {
                          onError: () => {
                            toast.error(t("Error tagging. Please try again."));
                          },
                        }
                      );
                    } else {
                      await updateFormMutation.mutateAsync(
                        {
                          id: form.id,
                          tags: form.tags?.filter((name) => name !== tag) ?? [],
                          _options: {
                            replaceObjectFields: true,
                          },
                        },
                        {
                          onError: () => {
                            toast.error(t("Error removing tag. Please try again."));
                          },
                        }
                      );
                    }
                  }}
                  model="forms"
                />
              ),
            },
          ],
        ]}
        primaryAction={
          contentToDisplay
            ? {
                label: t("Collapse"),
                onClick: () => {
                  setContentToDisplay(undefined);
                  setSelectedResponseId(undefined);
                },
              }
            : undefined
        }
        className={className}
      >
        {contentToDisplay === "view-responses" && (
          <div className="p-4">
            {selectedResponse ? (
              <>
                <FormResponse
                  timestamp={selectedResponse.submittedAt || selectedResponse.updatedAt}
                  response={selectedResponse}
                  enduserName={enduserName}
                  enduserId={currentEnduserId}
                  onGeneratePDFClick={() => pdfActions.generateAndDownload()}
                  onBack={() => {
                    setSelectedResponseId(undefined);
                  }}
                />
                {/* Off-screen printable content the PDF generator captures on demand. Rendered
                    whenever a response is selected (not gated on submittedAt) so the portal node —
                    and thus the generator's content ref — is always present when Generate PDF is
                    clicked, including for unsubmitted/draft responses. */}
                <pdfActions.PDFPortal>
                  <FormResponsePrintablePDF
                    submittedAt={selectedResponse.submittedAt || selectedResponse.updatedAt}
                    response={selectedResponse}
                    enduserName={enduserName}
                    hideLogo={true}
                  />
                </pdfActions.PDFPortal>
              </>
            ) : (
              <FormResponses
                contactId={currentEnduserId}
                formId={item.formId}
                onResponseSelect={({ id }) => setSelectedResponseId(id)}
              />
            )}
          </div>
        )}
      </TimelineInteractiveCard>
      <ConfirmDialog
        open={isDeleting}
        onOpenChange={setIsDeleting}
        title={t("Delete Responses")}
        description={t(
          "timeline.form_response_group.deleteDescription",
          "Delete all of this contact's responses to this form"
        )}
        confirmText={t("Delete Responses")}
        cancelText={t("Cancel")}
        onConfirm={() => {}}
        onCancel={() => {
          setIsDeleting(false);
        }}
        variant="destructive"
      />
      {pdfActions.isBusy && (
        <LoadingScreen
          text={t("Saving your PDF...")}
          className="absolute top-0 left-0 right-0 bottom-0 bg-gray-100 bg-opacity-80"
          variant="inline"
        />
      )}
    </>
  );
};
